import { Metadata } from "../models/metadata.model";

/** Unit separator; cannot appear in path, validity, or lang. */
export const BOOK_EDITION_KEY_SEPARATOR = "\u001f";

export type BookEditionKeyParts = {
    path: string;
    validityStep: string;
    lang: string;
};

/**
 * Normalize a root-relative site-file path for outgoing refs and edition keys.
 * Relative values, protocols, `/api/`, and hash-only refs are dropped (PC-2).
 */
export function normalizeSitePath(raw: string | null | undefined): string | null {
    if (raw == null) {
        return null;
    }
    const trimmed = raw.trim();
    if (!trimmed) {
        return null;
    }
    if (trimmed.startsWith("//")) {
        return null;
    }
    const lower = trimmed.toLowerCase();
    if (lower.startsWith("https:") || lower.startsWith("http:") || lower.startsWith("mailto:") || lower.startsWith("tel:")) {
        return null;
    }
    if (!trimmed.startsWith("/")) {
        return null;
    }
    const withoutHash = trimmed.split("#")[0];
    const withoutQuery = withoutHash.split("?")[0];
    if (!withoutQuery) {
        return null;
    }
    if (withoutQuery === "/api" || withoutQuery.startsWith("/api/")) {
        return null;
    }
    return withoutQuery;
}

export function encodeBookEditionKey(path: string, validityStep: string, lang?: string): string {
    const normalized = normalizeSitePath(path) ?? path.trim();
    return `${normalized}${BOOK_EDITION_KEY_SEPARATOR}${validityStep}${BOOK_EDITION_KEY_SEPARATOR}${lang ?? ""}`;
}

export function decodeBookEditionKey(key: string): BookEditionKeyParts | null {
    const parts = key.split(BOOK_EDITION_KEY_SEPARATOR);
    if (parts.length !== 3) {
        return null;
    }
    return { path: parts[0], validityStep: parts[1], lang: parts[2] };
}

export function getExplicitValiditySteps(metadata: Pick<Metadata, "validityInfo"> | null | undefined): string[] {
    const steps: string[] = [];
    const seen = new Set<string>();
    for (const entry of metadata?.validityInfo ?? []) {
        if (entry.info.isImplicit === false && !seen.has(entry.validity)) {
            seen.add(entry.validity);
            steps.push(entry.validity);
        }
    }
    return steps;
}

function isValidityPrefix(prefix: string, value: string): boolean {
    if (prefix === value) {
        return true;
    }
    if (prefix === "") {
        return true;
    }
    return value.startsWith(`${prefix}:`);
}

/**
 * Pick the single explicit validity step used to encode a query-time edition key (PC-5 / ADR §3).
 */
export function pickExplicitValidityStep(
    book: Pick<Metadata, "validityInfo"> | null | undefined,
    filterValidity: string | undefined,
): string | null {
    const explicit = getExplicitValiditySteps(book);
    if (explicit.length === 0) {
        return null;
    }
    const filter = filterValidity ?? "";
    if (explicit.includes(filter)) {
        return filter;
    }
    const prefixes = explicit.filter((step) => isValidityPrefix(step, filter));
    if (prefixes.length > 0) {
        return prefixes.reduce((longest, step) => (step.length >= longest.length ? step : longest));
    }
    const covering = explicit.filter((step) => isValidityPrefix(filter, step));
    if (covering.length > 0) {
        return covering.reduce((shortest, step) => (step.length <= shortest.length ? step : shortest));
    }
    return null;
}

/**
 * Unique normalized union of outgoingPaths and includes, first-occurrence order.
 * Un-normalizable values are dropped.
 */
export function bookMembershipTargets(
    outgoingPaths: string[] | undefined,
    includes: string[] | undefined,
): string[] {
    const seen = new Set<string>();
    const targets: string[] = [];
    for (const raw of [...(outgoingPaths ?? []), ...(includes ?? [])]) {
        const normalized = normalizeSitePath(raw);
        if (normalized && !seen.has(normalized)) {
            seen.add(normalized);
            targets.push(normalized);
        }
    }
    return targets;
}

export function uniqueStrings(values: string[]): string[] {
    const seen = new Set<string>();
    const result: string[] = [];
    for (const value of values) {
        if (!seen.has(value)) {
            seen.add(value);
            result.push(value);
        }
    }
    return result;
}

export function encodeExplicitBookEditionKeys(book: Pick<Metadata, "path" | "lang" | "validityInfo">): string[] {
    return getExplicitValiditySteps(book).map((step) => encodeBookEditionKey(book.path, step, book.lang));
}
